/**
 * Background color utilities for content sections
 */
import type { ExtendedMergedTheme } from '@/lib/theme/extendedTheme'

export type BackgroundColorToken =
  | 'white'
  | 'beige'
  | 'gray'
  | 'black'
  | 'brand-primary'

/**
 * Converts a background color token to its CSS hex value
 * @param token - The color token to convert
 * @param [extendedTheme] - Optional extended theme object for colors
 * @returns The CSS hex value or undefined if token is invalid
 */
export function tokenToCss(
  token: BackgroundColorToken | string | null,
  extendedTheme?: ExtendedMergedTheme
): string | undefined {
  if (!token) return undefined

  // Use absolute neutral colors from theme when available, otherwise fallback to hardcoded values
  const absoluteColors = extendedTheme?.absolute.neutral

  switch (token) {
    case 'white':
      return absoluteColors?.white ?? '#FFFFFF'
    case 'beige':
      return '#F7F1E3' // Keep beige as a custom color (not in neutral palette)
    case 'gray':
      return absoluteColors?.lightGray ?? '#EBEBEC'
    case 'black':
      return absoluteColors?.black ?? '#0D0D0D'
    case 'brand-primary':
      return extendedTheme?.colors.brand.primary
    default:
      return undefined
  }
}

/**
 * Determines the appropriate text color based on background color token
 * @param token - The background color token
 * @param secondary - Whether to use secondary text color variant
 * @returns The text color value to use
 */
export function pickTextColor(
  token: BackgroundColorToken | string | null,
  secondary = false
): 'white' | 'black' | 'primary' | 'secondary' {
  if (!token) return secondary ? 'secondary' : 'primary'
  if (token === 'black' || token === 'brand-primary' /* assume dark possible */)
    return 'white'
  return 'black'
}

/**
 * All available background color tokens
 */
export const BACKGROUND_COLOR_TOKENS: BackgroundColorToken[] = [
  'white',
  'beige',
  'gray',
  'black',
  'brand-primary',
]
